import type { ReportStatus } from '#shared/types';

/**
 * Every path in the app, in one place.
 *
 * Each resource base is **both** the API path (under `/api/v1`) and the SPA
 * route base — the server mounts `@Controller('patients')` and the router
 * mounts `patients`. That identity is what lets `ResourceListPage`,
 * `ResourceDetailPage` and `ResourceFormShell` take a single `path` prop, so it
 * is encoded here rather than dissolved.
 *
 * Route *patterns* stay in `router.tsx` as literals. They are a different shape
 * — relative, with `:id` placeholders — and there is exactly one of each, so
 * indirection there would obscure the one table that should read like a map.
 */
export const RESOURCE = {
  patients: '/patients',
  labTests: '/lab-tests',
  testParameters: '/test-parameters',
  /** Standard test procedures — the lab's SOPs. */
  testProcedures: '/test-procedures',
  panels: '/panels',
  reportComments: '/report-comments',
  reportRemarks: '/report-remarks',
  /** Microbiology catalogues: what a culture can isolate, and test against. */
  antibiotics: '/antibiotics',
  organisms: '/organisms',
  printTemplates: '/print-templates',
  departments: '/departments',
  consultants: '/consultants',
  reagentItems: '/reagent-items',
  reagentLots: '/reagent-lots',
  reagentRecipes: '/reagent-recipes',
  suppliers: '/suppliers',
  users: '/users',
  /** Named permission sets staff are given. */
  roles: '/roles',
  /** Reports have no list route; they are reached from a patient or the worklist. */
  reports: '/reports',
  /** Spreadsheet imports. A run is a resource: it is listed, opened and deleted. */
  imports: '/imports',
  /** Reagent purchase requisitions. */
  requisitions: '/requisitions',
  /** The clinical inbox: tests a doctor asked for. */
  orderRequests: '/order-requests',
  /** Money out — salaries, rent, diesel. Reagent cost is derived, not entered. */
  expenses: '/expenses',
} as const;

/**
 * Marks the landing after a registration, so the patient screen can offer the
 * slip once and then forget it.
 *
 * A query param rather than router state: it survives a refresh, it is
 * inspectable, and clearing it is a `replace` that leaves the back button alone.
 */
export const JUST_REGISTERED = 'registered';

/** The patient screen, arrived at straight from registering them. */
export const patientJustRegistered = (id: number | string): string =>
  `${routes.patient(id)}?${JUST_REGISTERED}=1`;

type ResourcePath = (typeof RESOURCE)[keyof typeof RESOURCE];

/** The two standard sub-routes every resource base supports. */
const newOf = (base: ResourcePath): string => `${base}/new`;
const editOf = (base: ResourcePath, id: number | string): string => `${base}/${id}/edit`;

interface WorklistFilters {
  status?: ReportStatus | undefined;
  overdue?: boolean | undefined;
  unpaid?: boolean | undefined;
  mine?: boolean | undefined;
}

/**
 * The worklist, optionally pre-filtered.
 *
 * The worklist reads its whole state from the query string, so every link into
 * it is a filter. Building them here keeps the param vocabulary in one place
 * instead of spread across the dashboard, the command palette and the server's
 * KPI tiles.
 */
export function worklistPath(filters: WorklistFilters = {}): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.overdue) params.set('overdue', 'true');
  if (filters.unpaid) params.set('unpaid', 'true');
  if (filters.mine) params.set('mine', 'true');

  const query = params.toString();
  return query ? `/worklist?${query}` : '/worklist';
}

export const routes = {
  dashboard: '/',
  login: '/login',
  worklist: worklistPath,

  patients: RESOURCE.patients,
  patient: (id: number | string) => `${RESOURCE.patients}/${id}`,
  patientNew: newOf(RESOURCE.patients),
  patientEdit: (id: number | string) => editOf(RESOURCE.patients, id),
  /**
   * The print screen, optionally opened with one test already selected.
   *
   * The report-entry screen passes the report it is showing, because "print
   * this" from a single result screen means that test — not every test the
   * patient has ever had. The screen still lists them all, so widening the
   * selection is one click.
   */
  patientPrint: (id: number | string, reportId?: number) =>
    reportId === undefined
      ? `${RESOURCE.patients}/${id}/print`
      : `${RESOURCE.patients}/${id}/print?report=${reportId}`,

  report: (id: number | string) => `${RESOURCE.reports}/${id}`,

  /**
   * The comparison screen: one patient, one test, every visit.
   *
   * Per-patient with a test selector rather than per-report, because one table
   * across two different lab tests has no shared rows to compare.
   */
  patientTrends: (id: number | string, testId?: number) =>
    testId === undefined
      ? `${RESOURCE.patients}/${id}/trends`
      : `${RESOURCE.patients}/${id}/trends?test=${testId}`,

  labTests: RESOURCE.labTests,
  labTest: (id: number | string) => `${RESOURCE.labTests}/${id}`,
  labTestNew: newOf(RESOURCE.labTests),

  testParameters: RESOURCE.testParameters,
  testParameter: (id: number | string) => `${RESOURCE.testParameters}/${id}`,
  testParameterNew: newOf(RESOURCE.testParameters),

  testProcedures: RESOURCE.testProcedures,
  testProcedure: (id: number | string) => `${RESOURCE.testProcedures}/${id}`,
  testProcedureNew: newOf(RESOURCE.testProcedures),
  testProcedureEdit: (id: number | string) => editOf(RESOURCE.testProcedures, id),

  panels: RESOURCE.panels,
  reportComments: RESOURCE.reportComments,
  reportRemarks: RESOURCE.reportRemarks,
  antibiotics: RESOURCE.antibiotics,
  organisms: RESOURCE.organisms,

  printTemplates: RESOURCE.printTemplates,
  printTemplate: (id: number | string) => `${RESOURCE.printTemplates}/${id}`,
  printTemplateNew: newOf(RESOURCE.printTemplates),

  departments: RESOURCE.departments,
  department: (id: number | string) => `${RESOURCE.departments}/${id}`,
  departmentNew: newOf(RESOURCE.departments),

  consultants: RESOURCE.consultants,
  consultant: (id: number | string) => `${RESOURCE.consultants}/${id}`,
  consultantNew: newOf(RESOURCE.consultants),

  reagentItems: RESOURCE.reagentItems,
  reagentItem: (id: number | string) => `${RESOURCE.reagentItems}/${id}`,
  reagentItemNew: newOf(RESOURCE.reagentItems),
  reagentLots: RESOURCE.reagentLots,
  reagentRecipes: RESOURCE.reagentRecipes,
  suppliers: RESOURCE.suppliers,
  supplier: (id: number | string) => `${RESOURCE.suppliers}/${id}`,
  supplierNew: newOf(RESOURCE.suppliers),
  /** The stock ledger. A screen over an append-only table — nothing to create. */
  stockLedger: '/stock-ledger',
  /** The lab-wide record of reports sent to patients on WhatsApp. */
  whatsappLog: '/whatsapp-log',
  /** Entering stock, grouped by bench — the spreadsheet's "New Entry" launcher. */
  stockEntry: '/stock-entry',

  /** The price list. A screen, not a resource — there is nothing to create. */
  rateList: '/rate-list',
  /** The lab's own details. A singleton, so it lives under settings. */
  companyDetail: '/settings/company',
  /** Download the lab's data as a workbook. */
  backup: '/settings/backup',
  databaseHealth: '/settings/database',

  users: RESOURCE.users,
  userNew: newOf(RESOURCE.users),

  roles: RESOURCE.roles,
  role: (id: number | string) => `${RESOURCE.roles}/${id}`,
  roleNew: newOf(RESOURCE.roles),
  roleEdit: (id: number | string) => editOf(RESOURCE.roles, id),

  imports: RESOURCE.imports,
  /** The four-step wizard. One route: the step is state, not a URL segment. */
  importRun: (id: number | string) => `${RESOURCE.imports}/${id}`,
  importNew: newOf(RESOURCE.imports),

  requisitions: RESOURCE.requisitions,
  requisition: (id: number | string) => `${RESOURCE.requisitions}/${id}`,
  requisitionNew: newOf(RESOURCE.requisitions),
  requisitionEdit: (id: number | string) => editOf(RESOURCE.requisitions, id),
  /** Recording a delivery against a requisition. */
  requisitionReceive: (id: number | string) => `${RESOURCE.requisitions}/${id}/receive`,

  orderRequests: RESOURCE.orderRequests,
  orderRequest: (id: number | string) => `${RESOURCE.orderRequests}/${id}`,
  orderRequestNew: newOf(RESOURCE.orderRequests),
  orderRequestEdit: (id: number | string) => editOf(RESOURCE.orderRequests, id),
  /** Turning a request into a real registration. */
  orderRequestRegister: (id: number | string) => `${RESOURCE.orderRequests}/${id}/register`,

  expenses: RESOURCE.expenses,
  expenseNew: newOf(RESOURCE.expenses),
  expenseEdit: (id: number | string) => editOf(RESOURCE.expenses, id),
  /** Revenue, broken down by day, test, bench, panel or who registered it. */
  income: '/income',
  profitLoss: '/profit-loss',
  yearHistory: '/year-history',
  /** The end-of-shift summary. Replaces the spreadsheet's Main Desk grid. */
  dayClose: '/day-close',
  /**
   * The charge calculator.
   *
   * The same `calculatePricing` the registration form and the server both use —
   * not a second implementation, or the two disagree in front of a patient.
   */
  calculator: '/calculator',

  /* ---------------------------------------------------------------------- *
   * Analytics
   * ---------------------------------------------------------------------- */

  /**
   * Operational analytics: volume, patients, flow, quality, staff, supply.
   *
   * Sections are child routes rather than tabs on one route, because they do
   * not share a permission — `RequireCapability` wraps route groups, and a
   * single route would have to re-implement the gating inside the component and
   * then duplicate it in the tab list.
   *
   * Revenue is not here. `IncomePage` already answers it, and a second income
   * screen would mean a second copy of `groupIncome` — which is exactly how two
   * screens end up disagreeing about the same month.
   */
  analytics: '/analytics',
  analyticsVolume: '/analytics/volume',
  analyticsPatients: '/analytics/patients',
  analyticsFlow: '/analytics/flow',
  analyticsQuality: '/analytics/quality',
  analyticsStaff: '/analytics/staff',
  analyticsSupply: '/analytics/supply',
} as const;
